import { useState, useEffect, useCallback } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Loader2, Search, X, Check, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

/**
 * User-facing dialog to suggest a cross-play link between two MDB games.
 * Submits via /api/multiplayer/cross-play-suggest (creates a bug_report for admin review).
 */
export default function CrossPlaySuggestionDialog({ open, onOpenChange, sourceGame }) {
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearchQ("");
      setSearchResults([]);
      setTarget(null);
      setNote("");
    }
  }, [open]);

  const runSearch = useCallback(async (q) => {
    if (!q.trim() || !sourceGame?.id) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/multiplayer/search-games`, {
        params: { q, exclude_id: sourceGame.id, limit: 20 },
      });
      setSearchResults(r.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [sourceGame?.id]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(searchQ), 250);
    return () => clearTimeout(t);
  }, [searchQ, runSearch]);

  const handleSubmit = async () => {
    if (!target) { toast.error("Pick the target game"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/multiplayer/cross-play-suggest`, {
        source_master_game_id: sourceGame.id,
        target_master_game_id: target.id,
        note: note.trim() || null,
      });
      toast.success("Suggestion sent — an admin will review it");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!sourceGame) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-violet-400" />
            Suggest a cross-play link
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-white/40 text-xs">
            Tell an admin that <span className="text-white/70">{sourceGame.name}</span> should share players & billboard with another version of the same game on a different platform.
          </p>

          <div>
            <p className="text-white/60 text-sm mb-1.5">Target game</p>
            {target ? (
              <div className="flex items-center gap-2 p-2 rounded bg-white/[0.05] border border-violet-500/30">
                {target.boxArtUrl && <img src={target.boxArtUrl} alt="" className="w-8 h-10 object-cover rounded" />}
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{target.name}</div>
                  <div className="text-white/40 text-xs truncate">
                    {(target.systems || target.platforms || []).slice(0, 4).join(" · ") || "—"}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setTarget(null)} className="text-white/40 hover:text-red-400 h-7 px-2" data-testid="cps-clear-target">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    data-testid="cps-search"
                    placeholder="Search MDB by name..."
                    value={searchQ}
                    onChange={e => setSearchQ(e.target.value)}
                    className="pl-8 bg-white/5 border-white/10 text-white text-sm"
                  />
                  {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 animate-spin" />}
                </div>
                {searchResults.length > 0 && (
                  <div className="space-y-1.5 mt-2 max-h-64 overflow-y-auto">
                    {searchResults.map(g => (
                      <button
                        key={g.id}
                        data-testid={`cps-pick-${g.id}`}
                        onClick={() => setTarget(g)}
                        className="w-full flex items-center gap-2 p-2 rounded bg-white/[0.03] border border-white/5 hover:border-white/20 transition-colors text-left"
                      >
                        {g.boxArtUrl && <img src={g.boxArtUrl} alt="" className="w-8 h-10 object-cover rounded shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm truncate">{g.name}</div>
                          <div className="text-white/40 text-xs truncate">
                            {(g.systems || g.platforms || []).slice(0, 4).join(" · ") || "—"}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchQ.trim() && !searching && searchResults.length === 0 && (
                  <p className="text-white/30 text-xs italic mt-2">No matches.</p>
                )}
              </>
            )}
          </div>

          <div>
            <p className="text-white/60 text-sm mb-1.5">Note (optional)</p>
            <Textarea
              data-testid="cps-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Why should these link? (e.g., crossplay was added in patch 2.1)"
              rows={3}
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/60 hover:text-white">Cancel</Button>
            <Button
              data-testid="cps-submit"
              onClick={handleSubmit}
              disabled={!target || submitting}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Submit Suggestion
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
